import React, { useCallback, useEffect, useRef, useState } from 'react'
import req from '@sportninja/common/api/request'
import * as inviteUtils from '@sportninja/common/components/Invite/utils'
import { ROUTES } from '@sportninja/common/constants/app'
import {
  hasDisplayableRelationship,
  isFamilyInvitation,
  relationshipLabel,
} from '@sportninja/common/features/profile/family/utils'
import { t } from '@sportninja/common/i18n'
import { getErrorMessage } from '@sportninja/common/utils/utils'

import { getIsPlayerInvite, normalizeBirthDate } from '../utils/inviteContext'
import type {
  InviteSignupFieldErrors,
  InviteSignupFormErrors,
  InviteSignupInvite,
  InviteSignupLoading,
  InviteSignupPageError,
  InviteSignupSuccess,
} from '../types'

type UseInviteSignupArgs = {
  inviteId: string
  isLoggedIn: boolean
  /** Bound redux actions, passed down from the connected screen. */
  readInvite: (id: string) => Promise<any>
  register: (body: Record<string, unknown>) => Promise<any>
  login: (credentials: { email: string; password: string }) => Promise<any>
  logout: () => Promise<any>
  setNewUserInviteId: (id: string) => void
  /** `history.push`, for the already-logged-in redirect. */
  redirect: (path: string) => void
}

const EMPTY_INVITE: InviteSignupInvite = {
  email: '',
  origNameFirst: null,
  origNameLast: null,
  inviterName: '',
  roleTypeId: false,
  type: '',
  inviteTypeId: null,
  isFamily: false,
  familyPlayerName: '',
  familyRelation: '',
  familyHasRelation: false,
}

const NO_FIELD_ERRORS: InviteSignupFieldErrors = {
  firstNameError: false,
  lastNameError: false,
  passwordError: false,
  dateOfBirthError: false,
  confirmPasswordError: false,
}

/**
 * All state and behavior for the invitation signup screen.
 *
 * This is a direct port of the legacy `InviteRegister.jsx` class body. Every
 * validation rule, early return, error mapping and request stays as it was —
 * only the storage moved from `this.state` to `useState`. Field names are
 * unchanged so the two can be diffed against each other.
 */
export const useInviteSignup = ({
  inviteId,
  isLoggedIn,
  readInvite,
  register,
  login,
  logout,
  setNewUserInviteId,
  redirect,
}: UseInviteSignupArgs) => {
  // --- form fields -----------------------------------------------------
  const [nameFirst, setNameFirst] = useState('')
  const [nameLast, setNameLast] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [birthDate, setBirthDate] = useState('')

  // --- invitation ------------------------------------------------------
  const [invite, setInvite] = useState<InviteSignupInvite>(EMPTY_INVITE)

  // --- status ----------------------------------------------------------
  const [loading, setLoading] = useState<InviteSignupLoading>({
    page: true,
    register: false,
  })
  const [success, setSuccess] = useState<InviteSignupSuccess>(false)
  const [pageError, setPageError] = useState<InviteSignupPageError>(false)
  const [shouldPushToUnderageFlow, setShouldPushToUnderageFlow] =
    useState(false)

  // --- errors ----------------------------------------------------------
  const [errors, setErrors] = useState<InviteSignupFormErrors>({
    email: false,
    password: false,
  })
  const [fieldErrors, setFieldErrors] =
    useState<InviteSignupFieldErrors>(NO_FIELD_ERRORS)

  // The legacy component recomputed this on every change and read it on submit.
  const passwordsMatch = password === confirmPassword

  const isPlayerInvite = getIsPlayerInvite(invite.inviteTypeId)

  // The redirect timer must not fire after unmount.
  const redirectTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(
    () => () => {
      if (redirectTimer.current) {
        clearTimeout(redirectTimer.current)
      }
    },
    []
  )

  // --- mount: read the invitation --------------------------------------
  useEffect(() => {
    let cancelled = false

    const run = async () => {
      try {
        if (isLoggedIn) {
          redirect(`${ROUTES.INVITE_V2_ROOT}${inviteId}`)
          return
        }

        const data = await inviteUtils.onInviteMount(inviteId, readInvite)
        if (cancelled) {
          return
        }

        // Route into the UnderageInvitation flow when the BE flags the invite
        // as underage. The flag is the sole trigger — a missing birth_date is
        // not treated as implicitly underage, since the standard register form
        // collects the DOB for player invites. Two exclusions:
        // - Non-player invites (no player_id): the underage workflow is only
        //   meaningful for a player being invited. Staff/official invites fall
        //   through to the standard register form.
        // - Family (user_to_player) invitations: the invited adult is being
        //   linked to a player and must land on the standard family register
        //   form, not the underage "Are you the player?" workflow.
        const isFamily = isFamilyInvitation(data)
        const isPlayer = !!data?.player_id
        if (!isFamily && isPlayer && data?.show_underage_workflow) {
          setShouldPushToUnderageFlow(true)
          setLoading({ page: false, register: false })
          return
        }

        const {
          invitee: { email, nameFirst: firstName, nameLast: lastName, type },
          inviter: { fullName },
          roleTypeId,
        } = inviteUtils.getInviteObject(data)

        setInvite({
          email,
          type,
          inviterName: fullName ? fullName.replace(/$the[/s]/i, '') : '',
          origNameFirst: firstName,
          origNameLast: lastName,
          roleTypeId,
          inviteTypeId: data?.type?.id ?? null,
          isFamily,
          familyPlayerName:
            data?.destination?.name_full || data?.destination?.name || '',
          familyRelation: relationshipLabel(data?.relationship_type),
          familyHasRelation: hasDisplayableRelationship(
            data?.relationship_type
          ),
        })

        // Prefill the editable name fields from the invite.
        setNameFirst(firstName || '')
        setNameLast(lastName || '')
        setBirthDate(normalizeBirthDate(data?.birth_date))

        setNewUserInviteId(inviteId)
      } catch (e) {
        if (cancelled) {
          return
        }

        console.error(e)
        let error = e
        if (error instanceof TypeError) {
          error = new Error(
            `${t('errors:pleaseTryRefreshingThePage', {})}. (E01)`
          )
        }
        setPageError(error as InviteSignupPageError)
      } finally {
        if (!cancelled) {
          setLoading((prev) => ({ ...prev, page: false }))
        }
      }
    }

    run()

    return () => {
      cancelled = true
    }
    // Runs once for the invite id this screen was mounted with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteId])

  /**
   * Clear the shared password error as the user types.
   *
   * The legacy `onChange` cleared `errors.password` on *every* field change,
   * not just the password ones — preserved here by routing all field writes
   * through this helper.
   */
  const clearPasswordError = useCallback(() => {
    setErrors((prev) => ({ ...prev, password: false }))
  }, [])

  const onChangeField = useCallback(
    (setter: (value: string) => void) => (value: string) => {
      clearPasswordError()
      setter(value)
    },
    [clearPasswordError]
  )

  /** Pre-flight validation the API performs before the account is created. */
  const validateFormUsingApi = useCallback(
    async (data: Record<string, unknown>) => {
      return req('/signup/signup-validate-mobile', {
        method: 'POST',
        body: JSON.stringify(data),
      })
    },
    []
  )

  /** Map the API's `invalid_fields` onto per-field state. */
  const setCustomErrors = useCallback(
    (invalidFields: Record<string, string>) => {
      setFieldErrors((prev) => ({
        ...prev,
        ...(invalidFields.name_first
          ? { firstNameError: invalidFields.name_first }
          : {}),
        ...(invalidFields.name_last
          ? { lastNameError: invalidFields.name_last }
          : {}),
        ...(invalidFields.birth_date
          ? { dateOfBirthError: invalidFields.birth_date }
          : {}),
        ...(invalidFields.password
          ? { passwordError: invalidFields.password }
          : {}),
        ...(invalidFields.confirm_password
          ? { confirmPasswordError: invalidFields.confirm_password }
          : {}),
      }))

      // Email has no field on this form, so it surfaces at form level.
      if (invalidFields.email) {
        setErrors((prev) => ({ ...prev, form: `${invalidFields.email}` }))
      }
    },
    []
  )

  const onSubmit = useCallback(
    async (event?: React.FormEvent) => {
      event?.preventDefault()

      // Only player invitations require a DOB. Missing one is a silent no-op,
      // exactly as before — no message, no request.
      if (isPlayerInvite && !birthDate) {
        return
      }

      if (!passwordsMatch) {
        setErrors((prev) => ({
          ...prev,
          password: t('errors:passwordsDoNotMatch', {}),
        }))
        return
      }

      try {
        setLoading((prev) => ({ ...prev, register: true }))
        setErrors((prev) => ({ ...prev, form: false, password: false }))
        setSuccess(false)
        setFieldErrors(NO_FIELD_ERRORS)

        const body: Record<string, unknown> = {
          email: invite.email,
          invitation_id: inviteId,
          name_first: nameFirst,
          name_last: nameLast,
          password,
          confirm_password: confirmPassword,
        }

        const dataToValidate: Record<string, unknown> = {
          email: invite.email,
          name_first: nameFirst,
          name_last: nameLast,
          password,
          confirm_password: confirmPassword,
        }

        if (isPlayerInvite) {
          const birthDateOnly = birthDate?.split('T')[0]
          body.birth_date = birthDateOnly
          dataToValidate.birth_date = birthDateOnly
        }

        await validateFormUsingApi(dataToValidate)

        const { user } = await register(body)

        if (user && typeof user.validated_at === 'string') {
          setSuccess('loading')
          await logout()
          await login({ email: invite.email, password })
          // Wait a little, so the user can actually see the success message
          redirectTimer.current = setTimeout(() => {
            setSuccess('redirect')
          }, 2000)
        } else {
          setSuccess('verification')
        }
      } catch (error: any) {
        if (error?.invalid_fields) {
          setCustomErrors(error.invalid_fields)
          setSuccess(false)
          setLoading((prev) => ({ ...prev, register: false }))
          return
        }

        const errorMessage = getErrorMessage(error)
        setErrors((prev) => ({
          ...prev,
          form: `${t(
            'errors:somethingWentWrongPleaseTryAgain',
            {}
          )} ${errorMessage}`,
        }))
        setLoading((prev) => ({ ...prev, register: false }))
      }
    },
    [
      birthDate,
      confirmPassword,
      invite.email,
      inviteId,
      isPlayerInvite,
      login,
      logout,
      nameFirst,
      nameLast,
      password,
      passwordsMatch,
      register,
      setCustomErrors,
      validateFormUsingApi,
    ]
  )

  return {
    // fields
    nameFirst,
    nameLast,
    password,
    confirmPassword,
    birthDate,
    setNameFirst: onChangeField(setNameFirst),
    setNameLast: onChangeField(setNameLast),
    setPassword: onChangeField(setPassword),
    setConfirmPassword: onChangeField(setConfirmPassword),
    setBirthDate: onChangeField(setBirthDate),

    // invitation
    invite,
    isPlayerInvite,

    // status
    loading,
    success,
    pageError,
    shouldPushToUnderageFlow,

    // errors
    errors,
    fieldErrors,
    passwordsMatch,

    // actions
    onSubmit,
  }
}
